import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/lib/AuthContext";
import { formatApiError } from "@/lib/api";
import { Truck, Loader2, Gift } from "lucide-react";
import { toast } from "sonner";

export default function AuthPage({ mode = "login" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  const isLogin = mode === "login" || location.pathname === "/login";
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", full_name: "", phone_number: "", referral_code: "" });

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref && !isLogin) setForm(f => ({ ...f, referral_code: ref.toUpperCase() }));
  }, [searchParams, isLogin]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
        toast.success("Velkommen tilbage!");
      } else {
        await register(form);
        toast.success(form.referral_code
          ? "Konto oprettet! Du får 50 kr. efter dit første job."
          : "Konto oprettet, velkommen!");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Noget gik galt");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute top-1/4 -left-20 w-72 h-72 bg-primary/10 blob" />
      <div className="absolute bottom-1/4 -right-20 w-56 h-56 bg-amber-200/40 blob" style={{ animationDelay: "2s" }} />

      <Card className="w-full max-w-md relative shadow-2xl shadow-primary/5 border-primary/10">
        <CardHeader className="text-center">
          <div className="w-12 h-12 rounded-2xl bg-primary mx-auto mb-3 flex items-center justify-center shadow-md shadow-primary/30">
            <Truck className="w-6 h-6 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl">{isLogin ? "Log ind på Grabber" : "Opret en Grabber-konto"}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {isLogin ? "Velkommen tilbage. Log ind for at fortsætte." : "Det tager kun et minut at komme i gang."}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="full_name">Fulde navn</Label>
                  <Input id="full_name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Anna Hansen" data-testid="register-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Telefonnummer <span className="text-muted-foreground text-xs">(valgfrit)</span></Label>
                  <Input id="phone" value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} placeholder="+45 12 34 56 78" />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="dig@email.dk" data-testid="auth-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pwd">Adgangskode</Label>
              <Input id="pwd" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" data-testid="auth-password-input" />
            </div>
            {!isLogin && (
              <div className="space-y-1.5">
                <Label htmlFor="ref" className="flex items-center gap-1.5">
                  <Gift className="w-3.5 h-3.5 text-primary" /> Invitations-kode <span className="text-muted-foreground text-xs">(valgfrit)</span>
                </Label>
                <Input
                  id="ref"
                  value={form.referral_code}
                  onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                  placeholder="F.eks. ABC1234"
                  className="font-mono tracking-wider uppercase"
                  data-testid="register-ref-input"
                />
                {form.referral_code && (
                  <p className="text-xs text-primary flex items-center gap-1">
                    ✨ Du får <strong>50 kr.</strong> bonus efter dit første job
                  </p>
                )}
              </div>
            )}
            <Button type="submit" className="w-full h-11 rounded-xl font-semibold shine-hover" disabled={loading} data-testid="auth-submit-btn">
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {isLogin ? "Log ind" : "Opret konto"}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              {isLogin ? (
                <>Ingen konto? <Link to="/register" className="text-primary font-medium hover:underline">Opret en</Link></>
              ) : (
                <>Har du allerede en konto? <Link to="/login" className="text-primary font-medium hover:underline">Log ind</Link></>
              )}
            </p>
            {isLogin && (
              <div className="text-center text-xs text-muted-foreground border-t border-border pt-3 mt-2">
                <p className="font-medium mb-1">Demo-konti til test:</p>
                <p>anna@grabber.dk / test123 (kunde)</p>
                <p>lars@grabber.dk / test123 (chauffør)</p>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
